import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, Home } from "lucide-react";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { loanPrograms, residentialPrograms } from "../mock";

function ProgramCard({ p, i }) {
  return (
    <Reveal delay={(i % 3) * 90}>
      <Link
        to={`/loan-programs/${p.slug}`}
        className="group block h-full rounded-2xl bg-white overflow-hidden ring-1 ring-slate-100 shadow-sm hover:shadow-2xl hover:-translate-y-1.5 transition-all duration-300"
      >
        <div className="relative h-52 overflow-hidden">
          <img src={p.image} alt={p.title} className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-500" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0b1f3a]/70 to-transparent" />
          <h3 className="absolute bottom-4 left-5 right-5 font-display font-bold text-lg text-white">{p.title}</h3>
        </div>
        <div className="p-6">
          <p className="text-sm text-slate-600 leading-relaxed">{p.short}</p>
          <div className="mt-5 flex flex-wrap gap-2">
            {p.highlights.slice(0, 2).map((h) => (
              <span key={h} className="text-xs font-medium bg-slate-100 text-slate-600 rounded-full px-3 py-1">{h}</span>
            ))}
          </div>
          <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#c6a15b]">
            View Details <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </span>
        </div>
      </Link>
    </Reveal>
  );
}

function SectionHeader({ icon: Icon, eyebrow, title, subtitle }) {
  return (
    <Reveal className="text-center max-w-3xl mx-auto">
      <div className="inline-flex items-center gap-2 rounded-full bg-[#0b1f3a] px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-[#c6a15b]">
        <Icon className="h-4 w-4" /> {eyebrow}
      </div>
      <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-[#0b1f3a] mt-4">{title}</h2>
      <p className="mt-4 text-slate-600 text-lg">{subtitle}</p>
    </Reveal>
  );
}

export default function LoanPrograms() {
  return (
    <main>
      <PageHero
        title="Loan Programs"
        crumb="Loan Programs"
        subtitle="Commercial and residential financing solutions customized to your unique needs — one partner for every loan."
      />

      {/* Commercial */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            icon={Building2}
            eyebrow="Commercial Lending"
            title="Commercial Loan Programs"
            subtitle="Financing for investment properties and businesses of every size — up to $100 Million."
          />
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {loanPrograms.map((p, i) => (
              <ProgramCard key={p.slug} p={p} i={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Residential */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            icon={Home}
            eyebrow="Residential Lending"
            title="Residential Mortgage Programs"
            subtitle="Now offering home loans too — FHA, VA, USDA, conventional, jumbo, and refinance options for homeowners."
          />
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {residentialPrograms.map((p, i) => (
              <ProgramCard key={p.slug} p={p} i={i} />
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
